/**
 * useInvoiceFormDerived
 *
 * Pure memoised values derived from the form state, parties and seller
 * details: selected party, supply type, address lists and PO options.
 */

import { useMemo } from 'react'

import {
  SupplyType as ProtoSupplyType,
  type Party,
  type Address,
} from '@/gen/proto'

import { isValidGSTIN } from '../../../types/billing/billing-utils'
import { determineSupplyType, formatAmount } from '../../../utils/billing-helpers'

import type { InvoiceFormProps } from './types'
import type { InvoiceFormState } from './useInvoiceFormState'
import type { SupplyType } from '../../../types/billing'

export interface InvoiceFormDerivedParams {
  state: InvoiceFormState
  parties: Party[]
  isVendor: boolean
  sellerDetails: InvoiceFormProps['sellerDetails']
}

export interface InvoiceFormDerived {
  orgCountryCode: string
  normalizedSellerStateCode: string
  selectedParty: Party | undefined
  supplyType: SupplyType
  allPartyAddresses: Address[]
  orgAddresses: Address[]
  shippingAddressOptions: Address[]
  customerPOOptions: Array<{ value: string; label: string; description: string }>
  /** Purchase from an unregistered supplier (no valid GSTIN) — reverse charge,
   *  so the supplier charges no tax. Drives a 0% default for line items. */
  isUrpPurchase: boolean
}

export function useInvoiceFormDerived(params: InvoiceFormDerivedParams): InvoiceFormDerived {
  const { state, parties, isVendor, sellerDetails } = params
  const { invoiceType, selectedPartyId, entityAddresses, customerPOs } = state

  const orgCountryCode = useMemo(() => {
    if (sellerDetails.gstin?.length === 15) { return 'IN' }
    return 'IN'
  }, [sellerDetails.gstin])

  const normalizedSellerStateCode = useMemo(
    () => computeNormalizedSellerStateCode(sellerDetails.stateCode, sellerDetails.gstin),
    [sellerDetails.stateCode, sellerDetails.gstin],
  )

  const selectedParty = useMemo(
    () => parties.find((p) => p.id === selectedPartyId),
    [parties, selectedPartyId],
  )

  const supplyType: SupplyType = useMemo(
    () => computeSupplyType({ invoiceType, selectedParty, normalizedSellerStateCode }),
    [invoiceType, selectedParty, normalizedSellerStateCode],
  )

  const allPartyAddresses = useMemo(
    () => buildAllPartyAddresses(entityAddresses, selectedParty),
    [selectedParty?.billingAddress, selectedParty?.shippingAddress, entityAddresses], // eslint-disable-line react-hooks/exhaustive-deps
  )

  const orgAddresses = useMemo(
    (): Address[] => buildOrgAddresses(isVendor, sellerDetails?.entityAddresses),
    [isVendor, sellerDetails?.entityAddresses],
  )

  const shippingAddressOptions = useMemo(
    () => (isVendor ? orgAddresses : allPartyAddresses),
    [isVendor, orgAddresses, allPartyAddresses],
  )

  const customerPOOptions = useMemo(
    () => customerPOs.map((po) => ({
      value: po.id,
      label: po.customerPoNumber,
      description: `${formatAmount(po.balance, po.currency)} balance | ${po.customerPoDate}`,
    })),
    [customerPOs],
  )

  const isUrpPurchase = useMemo(
    () => computeIsUrpPurchase(isVendor, invoiceType, selectedParty, state.isWalkIn),
    [isVendor, invoiceType, selectedParty, state.isWalkIn],
  )

  return {
    orgCountryCode, normalizedSellerStateCode, selectedParty, supplyType,
    allPartyAddresses, orgAddresses, shippingAddressOptions, customerPOOptions,
    isUrpPurchase,
  }
}

// URP = purchase (vendor) from a supplier without a valid GSTIN — the supplier
// charges no tax. This covers two cases:
//   - a walk-in cash purchase (no party at all, hence no GSTIN), and
//   - a selected party whose party/billing-address GSTIN is missing or invalid.
// Export purchases (overseas) are handled separately. isValidGSTIN treats '' as
// valid, so registered requires a non-empty, valid GSTIN.
function computeIsUrpPurchase(
  isVendor: boolean,
  invoiceType: string,
  selectedParty: Party | undefined,
  isWalkIn: boolean,
): boolean {
  if (!isVendor || invoiceType === 'export') { return false }
  if (isWalkIn) { return true }
  if (!selectedParty) { return false }
  const gstin = (selectedParty.gstin ?? selectedParty.billingAddress?.gstin ?? '').trim()
  return !(gstin !== '' && isValidGSTIN(gstin))
}

function computeSupplyType(args: {
  invoiceType: string
  selectedParty: Party | undefined
  normalizedSellerStateCode: string
}): SupplyType {
  const { invoiceType, selectedParty, normalizedSellerStateCode } = args
  if (invoiceType === 'export') { return 'export' }
  if (!selectedParty?.gstin) { return 'interstate' }

  let buyerStateCode = selectedParty.gstin.substring(0, 2)
  const addressStateCode = selectedParty.billingAddress?.stateCode
  if (addressStateCode && /^\d+$/.test(addressStateCode)) {
    buyerStateCode = addressStateCode
  }

  const protoSupplyType = determineSupplyType(normalizedSellerStateCode, buyerStateCode, false)
  return protoSupplyType === ProtoSupplyType.SUPPLY_TYPE_INTRASTATE ? 'intrastate' : 'interstate'
}

function buildAllPartyAddresses(
  entityAddresses: Address[],
  selectedParty: Party | undefined,
): Address[] {
  // Entity addresses are the canonical source; proto slots (billingAddress/shippingAddress)
  // are a subset of the same entity addresses, so prefer entity addresses to avoid duplicates.
  // Fall back to proto slots only when entity addresses haven't been fetched yet.
  if (entityAddresses.length > 0) { return entityAddresses }
  const partyAddrs: Address[] = []
  if (selectedParty?.billingAddress) { partyAddrs.push(selectedParty.billingAddress) }
  if (selectedParty?.shippingAddress) {
    const isAlreadyAdded = partyAddrs.some(a => a.id && a.id === selectedParty.shippingAddress?.id)
    if (!isAlreadyAdded) { partyAddrs.push(selectedParty.shippingAddress) }
  }
  return partyAddrs
}

function buildOrgAddresses(
  isVendor: boolean,
  entityAddresses: InvoiceFormProps['sellerDetails']['entityAddresses'],
): Address[] {
  if (!isVendor || !entityAddresses) { return [] }
  return entityAddresses.map((addr, index) => toOrgAddress(addr, index))
}

type EntityAddrInput = NonNullable<InvoiceFormProps['sellerDetails']['entityAddresses']>[number]

const ORG_STRING_FIELDS = [
  'line1', 'line2', 'city', 'state', 'stateCode', 'postalCode',
  'country', 'gstin', 'companyCode',
] as const

function orgStringFields(addr: EntityAddrInput): Record<(typeof ORG_STRING_FIELDS)[number], string> {
  const result = {} as Record<(typeof ORG_STRING_FIELDS)[number], string>
  for (const key of ORG_STRING_FIELDS) {
    result[key] = ((addr as Record<string, unknown>)[key] as string | undefined) ?? ''
  }
  return result
}

function toOrgAddress(addr: EntityAddrInput, index: number): Address {
  return {
    id: `org-${addr.id ?? index}`,
    label: addr.label ?? '',
    type: addr.label ?? 'shipping',
    ...orgStringFields(addr),
    countryCode: addr.countryCode ?? 'IN',
    isPrimary: addr.isPrimary ?? false,
  }
}

// =============================================================================
// Helpers
// =============================================================================

function computeNormalizedSellerStateCode(
  stateCode: string | undefined,
  gstin: string | undefined,
): string {
  if (stateCode && /^\d+$/.test(stateCode)) { return stateCode }

  if (gstin && gstin.length >= 2) {
    return gstin.substring(0, 2)
  }

  const stateCodeMap: Record<string, string> = {
    KL: '32',
    TN: '33',
    KA: '29',
    MH: '27',
    DL: '07',
  }

  if (stateCode && stateCodeMap[stateCode.toUpperCase()]) {
    return stateCodeMap[stateCode.toUpperCase()] ?? ''
  }

  return stateCode ?? ''
}
